import React, { useState, useEffect } from "react";
import { Appliance, getOptimizedImageUrl, parseImageUrls, getProductBgStyle, getProductOrientation } from "../types";
import { getImageLoadingProps, generateSrcSet } from "../utils/imageLoader";

export function formatCleanTitle(rawName: string): string {
  if (!rawName) return "";
  let name = String(rawName);
  // 1. Remove pricing parentheticals: (₹600/mo | Rent), (₹700/mo), (₹1,400/mo), etc.
  name = name.replace(/\(\s*₹[^)]+\)/gi, "");
  name = name.replace(/\|\s*₹[^|]+/gi, "");
  // 2. Remove repetitive city / marketing suffixes: "on Rent in Bhopal", "on Rent in Sehore", "on Rent", "on Rental"
  name = name.replace(/\s+on\s+rent\s+in\s+[a-zA-Z\s]+/gi, "");
  name = name.replace(/\s+on\s+rent/gi, "");
  name = name.replace(/\s+on\s+rental/gi, "");
  name = name.replace(/\s+for\s+rent/gi, "");
  name = name.replace(/\s*\|\s*Rentlow/gi, "");
  // 3. Normalize specific lengthy labels
  name = name.replace(/Litres\s+Plastic\s+Body/gi, "Litres");
  name = name.replace(/Single\s+Door\s+Refrigerator/gi, "Refrigerator");
  name = name.replace(/\s{2,}/g, " ").replace(/\s*[-|:]\s*$/, "").trim();
  return name;
}

interface ApplianceCardProps {
  key?: string;
  appliance: Appliance;
  categoryName?: string;
  index?: number;
  isWishlisted?: boolean;
  onSelect: (appliance: Appliance) => void;
  onAddToCart?: (appliance: Appliance, e?: React.MouseEvent) => void;
  onToggleWishlist?: (appliance: Appliance, e?: React.MouseEvent) => void;
}

export default function ApplianceCard({
  appliance,
  index,
  onSelect
}: ApplianceCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});
  const [detectedOrientation, setDetectedOrientation] = useState<'landscape' | 'portrait' | null>(null);

  const cleanTitle = formatCleanTitle(appliance?.name || "");

  // Determine orientation type: Landscape vs Portrait
  const orientation = appliance.imageOrientation || detectedOrientation || getProductOrientation(appliance);
  const isLandscape = orientation === "landscape";

  const parsedImages = parseImageUrls(appliance?.images);
  const safeImages = parsedImages.length > 0
    ? parsedImages
    : ["https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80"];

  useEffect(() => {
    if (safeImages.length <= 1) return;

    // Cycle through images slowly with delayed start
    const idHash = (appliance.id || "").split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const initialDelay = 4000 + (idHash % 6) * 1000;

    let intervalId: NodeJS.Timeout;
    const startInterval = () => {
      intervalId = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % safeImages.length);
      }, 4500);
    };

    const timeoutId = setTimeout(startInterval, initialDelay);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [safeImages.length, appliance.id]);

  // Rent scheme determination
  const isDailyOnly = appliance.rentType === "day";
  
  // Primary display price
  const displayPrice = isDailyOnly 
    ? (appliance.dailyRentalPrice || appliance.rentalPrice || 0)
    : appliance.rentalPrice;

  // Background styling customized for Landscape vs Portrait
  const baseBgStyle = getProductBgStyle(appliance.backgroundImage || appliance.cardBgStyle);
  const defaultBgStyle = isLandscape
    ? { background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 50%, #e2e8f0 100%)" }
    : { background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 60%, #e2e8f0 100%)" };
  const effectiveBgStyle = (appliance.backgroundImage || appliance.cardBgStyle)
    ? baseBgStyle
    : defaultBgStyle;

  return (
    <div 
      onClick={() => onSelect(appliance)}
      className={`h-full cursor-pointer group flex flex-col ${
        isLandscape 
          ? "col-span-2 col-span-full w-full -mx-4 sm:mx-0 w-[calc(100%+2rem)] sm:w-full my-3 sm:my-4" 
          : "col-span-1"
      }`}
    >
      {/* UNIFIED RESPONSIVE APPLIANCE CARD WITH ORIENTATION AWARENESS */}
      <div className={`flex ${isLandscape ? "flex-col md:flex-row" : "flex-col"} h-full bg-white ${isLandscape ? "rounded-none sm:rounded-2xl border-y sm:border shadow-sm sm:shadow-2xs" : "rounded-2xl border shadow-2xs"} border-slate-200/90 hover:border-[#0077B6]/60 hover:shadow-xl hover:shadow-[#0077B6]/10 transition-all duration-300 overflow-hidden relative`}>
        
        {/* 1. PRODUCT IMAGE STAGE (2 TYPES: LANDSCAPE vs PORTRAIT) */}
        {isLandscape ? (
          /* --- TYPE A: LANDSCAPE PRODUCT IMAGE (Covers complete screen between portrait items) --- */
          <div 
            className="relative w-full md:w-3/5 lg:w-2/3 h-56 xs:h-64 sm:h-72 md:h-80 lg:h-96 overflow-hidden flex items-center justify-center shrink-0 border-b md:border-b-0 md:border-r border-slate-100"
            style={effectiveBgStyle}
          >

            <div 
              className="flex h-full w-full transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {safeImages.map((img, idx) => {
                const isFirstRow = typeof index === "number" ? index < 4 : false;
                const loadingProps = getImageLoadingProps({
                  priority: isFirstRow && idx === 0,
                  index: idx,
                });
                const srcSet = generateSrcSet(img, [360, 480, 640]);
                const isLoaded = loadedImages[`d_${idx}`];

                return (
                  <div key={idx} className="w-full h-full flex-shrink-0 relative overflow-hidden flex items-center justify-center p-3.5 sm:p-5 sm:px-6">
                    {!isLoaded && !isFirstRow && (
                      <div className="absolute inset-0 bg-slate-200/70 animate-pulse" />
                    )}
                    <img
                      src={getOptimizedImageUrl(img, 600, 80)}
                      srcSet={srcSet || undefined}
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      width={480}
                      height={300}
                      alt={`Rent ${cleanTitle} in Bhopal and Sehore`}
                      {...loadingProps}
                      onLoad={(e) => {
                        setLoadedImages((prev) => ({ ...prev, [`d_${idx}`]: true }));
                        if (!appliance.imageOrientation && e.currentTarget.naturalWidth) {
                          const ratio = e.currentTarget.naturalWidth / (e.currentTarget.naturalHeight || 1);
                          setDetectedOrientation(ratio > 1.15 ? "landscape" : "portrait");
                        }
                      }}
                      className={`max-h-full max-w-full object-contain group-hover:scale-105 transition-all duration-500 drop-shadow-[0_8px_18px_rgba(0,0,0,0.1)] ${
                        isLoaded || isFirstRow ? "opacity-100" : "opacity-0"
                      }`}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80";
                        setLoadedImages((prev) => ({ ...prev, [`d_${idx}`]: true }));
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Subtle Carousel Dots Overlay */}
            {safeImages.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/30 backdrop-blur-xs px-2 py-0.5 rounded-full z-10 pointer-events-none">
                {safeImages.map((_, dotIdx) => (
                  <span
                    key={dotIdx}
                    className={`rounded-full transition-all duration-300 ${
                      currentImageIndex === dotIdx ? "bg-white w-2.5 h-1" : "bg-white/50 w-1 h-1"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          /* --- TYPE B: PORTRAIT PRODUCT IMAGE (Optimized for Fridges, Wardrobes, Desert Coolers, Water Purifiers, Heaters) --- */
          <div 
            className="relative h-56 xs:h-64 sm:h-72 md:h-80 lg:h-88 w-full overflow-hidden flex items-center justify-center shrink-0 border-b border-slate-100"
            style={effectiveBgStyle}
          >


            <div 
              className="flex h-full w-full transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
            >
              {safeImages.map((img, idx) => {
                const isFirstRow = typeof index === "number" ? index < 4 : false;
                const loadingProps = getImageLoadingProps({
                  priority: isFirstRow && idx === 0,
                  index: idx,
                });
                const srcSet = generateSrcSet(img, [360, 480, 640]);
                const isLoaded = loadedImages[`d_${idx}`];

                return (
                  <div key={idx} className="w-full h-full flex-shrink-0 relative overflow-hidden flex items-center justify-center p-3 sm:p-4.5">
                    {!isLoaded && !isFirstRow && (
                      <div className="absolute inset-0 bg-slate-200/70 animate-pulse" />
                    )}
                    <img
                      src={getOptimizedImageUrl(img, 600, 80)}
                      srcSet={srcSet || undefined}
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                      width={400}
                      height={480}
                      alt={`Rent ${cleanTitle} in Bhopal and Sehore`}
                      {...loadingProps}
                      onLoad={(e) => {
                        setLoadedImages((prev) => ({ ...prev, [`d_${idx}`]: true }));
                        if (!appliance.imageOrientation && e.currentTarget.naturalWidth) {
                          const ratio = e.currentTarget.naturalWidth / (e.currentTarget.naturalHeight || 1);
                          setDetectedOrientation(ratio > 1.15 ? "landscape" : "portrait");
                        }
                      }}
                      className={`max-h-full max-w-full object-contain group-hover:scale-105 transition-all duration-500 drop-shadow-[0_10px_20px_rgba(0,0,0,0.08)] ${
                        isLoaded || isFirstRow ? "opacity-100" : "opacity-0"
                      }`}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80";
                        setLoadedImages((prev) => ({ ...prev, [`d_${idx}`]: true }));
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Subtle Carousel Dots Overlay */}
            {safeImages.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/30 backdrop-blur-xs px-2 py-0.5 rounded-full z-10 pointer-events-none">
                {safeImages.map((_, dotIdx) => (
                  <span
                    key={dotIdx}
                    className={`rounded-full transition-all duration-300 ${
                      currentImageIndex === dotIdx ? "bg-white w-2.5 h-1" : "bg-white/50 w-1 h-1"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* 2. PRODUCT CONTENTS - ONLY Product Name, Rent, Refundable Security, and Book Now option */}
        <div className="p-2.5 sm:p-4 flex flex-col flex-1 justify-between bg-white text-left font-sans space-y-2 sm:space-y-2.5">
          <div className="space-y-1.5 sm:space-y-2 text-left">
            {/* Product Name */}
            <h3 className="font-sans font-bold text-slate-900 text-xs sm:text-sm leading-snug line-clamp-2 group-hover:text-[#0077B6] transition-colors duration-150 min-h-[2.3rem] text-left">
              {cleanTitle}
            </h3>

            {/* Rent / month or per day */}
            <div className="pt-0.5">
              <span className="block text-[10px] sm:text-[11px] font-semibold text-slate-400 uppercase tracking-wider font-sans">
                {isDailyOnly ? "Rent / day" : "Rent / month"}
              </span>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="text-base sm:text-lg font-black text-[#0077B6] font-sans tabular-nums leading-none">
                  ₹{displayPrice.toLocaleString("en-IN")}
                </span>
                <span className="text-[11px] sm:text-xs font-semibold text-slate-500 font-sans">
                  {isDailyOnly ? "/day" : "/mo"}
                </span>
              </div>
            </div>

            {/* Refundable Security Deposit - Fully visible on all mobile screen sizes */}
            <div className="pt-1.5 border-t border-slate-100 flex items-center justify-between gap-1 text-[11px] sm:text-xs">
              <span className="text-slate-500 font-medium text-[10.5px] sm:text-[11px] shrink min-w-0 truncate" title="Refundable Security Deposit">
                Refundable Deposit
              </span>
              <span className="font-bold text-slate-900 font-sans tabular-nums text-xs sm:text-xs shrink-0 whitespace-nowrap">
                ₹{appliance.securityDeposit.toLocaleString("en-IN")}
              </span>
            </div>
          </div>

          {/* Book Now Option Only */}
          <div className="pt-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(appliance);
              }}
              aria-label={`Book ${cleanTitle} now`}
              className="w-full h-10 sm:h-11 min-h-[40px] sm:min-h-[44px] bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white font-extrabold text-xs sm:text-sm px-3 sm:px-4 rounded-xl flex items-center justify-center gap-1.5 transition-all duration-150 cursor-pointer shadow-sm hover:shadow-md active:scale-[0.98] font-sans whitespace-nowrap"
            >
              <span>Book Now</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
